import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import APIPerformancePanel from '../components/Admin/APIPerformancePanel';
import AgentStatusPanel from '../components/Admin/AgentStatusPanel';
import HealthCheckPanel from '../components/Admin/HealthCheckPanel';
import PlatformStatsPanel from '../components/Admin/PlatformStatsPanel';
import RevenuePanel from '../components/Admin/RevenuePanel';
import UserAnalyticsPanel from '../components/Admin/UserAnalyticsPanel';
import { API_BASE_URL } from '../config';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    system_health: null,
    user_analytics: null,
    platform_stats: null,
    api_performance: null,
    agent_status: null,
    revenue_analytics: null
  });
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch all metrics
  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get auth token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        // Redirect to admin login
        navigate('/admin/login');
        return;
      }

      // Check if user is admin
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.role !== 'admin') {
          setError('Admin access required. You do not have admin privileges.');
          setTimeout(() => navigate('/admin/login'), 3000);
          return;
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/all-metrics`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401 || response.status === 403) {
        // Not authorized - redirect to admin login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setError('Admin authentication failed. Please login again.');
        setTimeout(() => navigate('/admin/login'), 2000);
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch metrics: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setMetrics(data.data);
        setLastUpdate(new Date());
      } else {
        setError(data.error || 'Failed to load metrics');
      }

    } catch (err) {
      console.error('Error fetching admin metrics:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchMetrics();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchMetrics();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Manual refresh
  const handleRefresh = () => {
    fetchMetrics();
  };

  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
  };

  if (loading && !metrics.system_health) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Admin Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Master Admin Dashboard</h1>
              <p className="text-sm text-gray-600 mt-1">
                AI Recruiter Platform Monitoring
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Auto-refresh toggle */}
              <button
                onClick={toggleAutoRefresh}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  autoRefresh
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {autoRefresh ? '🔄 Auto-refresh ON' : '⏸ Auto-refresh OFF'}
              </button>

              {/* Manual refresh button */}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Refreshing...' : '↻ Refresh'}
              </button>

              {/* Back to main app */}
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                ← Back to App
              </button>
            </div>
          </div>

          {/* Last update time */}
          {lastUpdate && (
            <div className="mt-3 text-xs text-gray-500">
              Last updated: {lastUpdate.toLocaleString()}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                ⚠ Error: {error}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions - Market Intelligence Editorial Office */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
          <h2 className="text-xl font-bold mb-3">Market Intelligence Editorial Office</h2>
          <p className="text-orange-100 mb-4">Draft, review, approve and publish evidence-led market intelligence</p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/admin/newsroom')}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 font-medium transition-colors"
            >
              Plan Newsroom
            </button>
            <button
              onClick={() => navigate('/admin/market-intelligence')}
              className="px-4 py-2 bg-white text-orange-600 rounded-lg hover:bg-orange-50 font-medium transition-colors"
            >
              Manage Intelligence
            </button>
            <button
              onClick={() => navigate('/admin/market-intelligence/new')}
              className="px-4 py-2 bg-orange-700 text-white rounded-lg hover:bg-orange-800 font-medium transition-colors"
            >
              Create Intelligence Article
            </button>
            <button
              onClick={() => navigate('/admin/categories')}
              className="px-4 py-2 bg-white text-orange-600 rounded-lg hover:bg-orange-50 font-medium transition-colors"
            >
              🏷️ Manage Categories
            </button>
            <button
              onClick={() => navigate('/market-intelligence')}
              className="px-4 py-2 bg-white text-orange-600 rounded-lg hover:bg-orange-50 font-medium transition-colors"
            >
              View Public Hub
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Row 1: Health Check & User Analytics */}
          <HealthCheckPanel
            healthData={metrics.system_health}
            loading={loading}
          />
          <UserAnalyticsPanel
            userData={metrics.user_analytics}
            loading={loading}
          />

          {/* Row 2: Platform Stats & API Performance */}
          <PlatformStatsPanel
            platformData={metrics.platform_stats}
            loading={loading}
          />
          <APIPerformancePanel
            apiData={metrics.api_performance}
            loading={loading}
          />

          {/* Row 3: Agent Status & Revenue (full width) */}
          <AgentStatusPanel
            agentData={metrics.agent_status}
            loading={loading}
          />
          <RevenuePanel
            revenueData={metrics.revenue_analytics}
            loading={loading}
          />
        </div>

        {/* System Info Footer */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-sm text-gray-600">System Status</div>
              <div className="text-lg font-bold text-green-600">
                {metrics.system_health?.database_status === 'healthy' ? 'Operational' : 'Issues Detected'}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Total API Calls</div>
              <div className="text-lg font-bold text-blue-600">
                {metrics.api_performance?.total_requests_today?.toLocaleString() || 0}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Platform Uptime</div>
              <div className="text-lg font-bold text-green-600">99.8%</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Active Agents</div>
              <div className="text-lg font-bold text-purple-600">
                {metrics.agent_status?.agents ?
                  Object.values(metrics.agent_status.agents).filter(a => a.status === 'active').length : 0} / 4
              </div>
            </div>
          </div>
        </div>

        {/* Documentation Link */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            📚 Need help? Check the{' '}
            <span className="text-orange-500 font-medium cursor-pointer hover:underline">
              Admin Dashboard Documentation
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

